import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import { useTranslation } from 'react-i18next';
import '../style/Navbar.css'; // Import the CSS file for layout-specific styles

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { user } = useAuthContext();
    const menuRef = useRef(null);
    const burgerRef = useRef(null);
    const { t, i18n } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

    // Persist language selection
    useEffect(() => {
        const storedLang = localStorage.getItem('language');
        if (storedLang && storedLang !== i18n.language) {
            i18n.changeLanguage(storedLang);
            setSelectedLanguage(storedLang);
        }
    }, [i18n]);

    const handleLanguageChange = (lang) => {
        i18n.changeLanguage(lang);
        setSelectedLanguage(lang);
        localStorage.setItem('language', lang);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            // Ensure the click is not on the burger button or inside the menu
            if (
                burgerRef.current &&
                burgerRef.current.contains(event.target)
            ) {
                return; // Ignore clicks on the burger button
            }
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target)
            ) {
                setIsMenuOpen(false); // Close the menu if clicked outside
            }
        };
    
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    
    const handleLinkClick = () => {
        setIsMenuOpen(false);
    };

    const handleBurgerClick = () => {
        setIsMenuOpen((prevState) => {
            const newState = !prevState;
            console.log(
                `Menu toggled from ${
                    prevState ? 'open' : 'closed'
                } to ${newState ? 'open' : 'closed'}`
            );
            return newState;
        });
    };

    return (
        <nav className="navbar bg-dafauna-primary text-dafauna-white shadow-md">
            <div className="container mx-auto flex justify-between items-center px-4 py-3">
                {/* Logo */}
                <Link to="/" className="navbar-logo text-2xl font-bold">
                    🐾 {t('navbar.logo')}
                </Link>



                {/* Burger Menu for Mobile */}
                <button
                    ref={burgerRef}
                    className="navbar-burger lg:hidden flex flex-col items-center justify-center space-y-1 focus:outline-none"
                    onClick={handleBurgerClick}
                >
                    <div
                        className={`w-6 h-1 bg-dafauna-white rounded transition-transform ${
                            isMenuOpen ? 'rotate-45 translate-y-2' : ''
                        }`}
                    ></div>
                    <div
                        className={`w-6 h-1 bg-dafauna-white rounded transition-opacity ${
                            isMenuOpen ? 'opacity-0' : ''
                        }`}
                    ></div>
                    <div
                        className={`w-6 h-1 bg-dafauna-white rounded transition-transform ${
                            isMenuOpen ? '-rotate-45 -translate-y-2' : ''
                        }`}
                    ></div>
                </button>

                {/* Links */}
                <div
                    ref={menuRef}
                    className={`navbar-mobile-menu ${
                        isMenuOpen ? 'flex' : 'hidden'
                    } lg:flex lg:flex-row lg:items-center lg:space-x-6 flex-col absolute lg:static top-16 left-0 w-full lg:w-auto bg-dafauna-primary lg:bg-transparent shadow-lg lg:shadow-none z-10`}
                >
                    <Link
                        to="/found-pets"
                        className="navbar-menu px-4 py-2 hover:text-dafauna-primaryLight"
                        onClick={handleLinkClick}
                    >
                        {t('navbar.foundPets')}
                    </Link>
                    <Link
                        to="/lost-pets"
                        className="navbar-menu px-4 py-2 hover:text-dafauna-primaryLight"
                        onClick={handleLinkClick}
                    >
                        {t('navbar.lostPets')}
                    </Link>
                    {user && (
                        <>
                            <Link
                                to={`/account/${user._id}`}
                                className="navbar-menu px-4 py-2 hover:text-dafauna-primaryLight"
                                onClick={handleLinkClick}
                            >
                                {t('navbar.myAccount')}
                            </Link>
                            <Link
                                to={`/settings/${user._id}`}
                                className="navbar-menu px-4 py-2 hover:text-dafauna-primaryLight"
                                onClick={handleLinkClick}
                            >
                                {t('navbar.settings')}
                            </Link>
                        </>
                    )}
                    {!user && (
                        <>
                            <Link
                                to="/login"
                                className="navbar-menu px-4 py-2 hover:text-dafauna-primaryLight"
                                onClick={handleLinkClick}
                            >
                                {t('navbar.login')}
                            </Link>
                            <div className="flex items-center space-x-2 ml-2" style={{ marginBottom: 5 }}>
                                <select
                                    value={selectedLanguage}
                                    onChange={(e) => handleLanguageChange(e.target.value)}
                                    className="language-dropdown border border-gray-300 rounded-md px-2 py-1 text-sm text-dafauna-darkText"
                                >
                                    <option value="es">🇪🇸 Español</option>
                                    <option value="en">🇺🇸 English</option>
                                </select>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </nav>
    );
};

export default Navbar;